export interface HourlyForecastItem {
  time: string
  temp: number
  code: number
  emoji: string
  label: string
}

export interface DailyForecastItem {
  date: string
  dayName: string
  minTemp: number
  maxTemp: number
  code: number
  condition: string
  emoji: string
}

export interface WeatherData {
  cityName: string
  currentTemp: number
  apparentTemp: number
  conditionLabel: string
  conditionEmoji: string
  weatherCode: number
  isDay: boolean
  humidity: number
  windSpeed: number
  uvIndex: number
  minTempToday: number
  maxTempToday: number
  hourly: HourlyForecastItem[]
  daily: DailyForecastItem[]
  lastUpdated: number
}

export function getWeatherDetails(code: number, isDay: number = 1, lang: string = "pt") {
  const isPt = lang === "pt"
  const isEs = lang === "es"

  switch (code) {
    case 0:
      return {
        label: isPt ? (isDay ? "Céu Limpo" : "Noite Limpa") : isEs ? "Cielo Despejado" : "Clear Sky",
        emoji: isDay ? "☀️" : "🌙",
        iconType: isDay ? "Sun" : "Moon",
      }
    case 1:
      return {
        label: isPt ? "Ensolarado" : isEs ? "Soleado" : "Mainly Sunny",
        emoji: isDay ? "🌤️" : "🌙",
        iconType: isDay ? "Sun" : "Moon",
      }
    case 2:
      return {
        label: isPt ? "Parcialmente Nublado" : isEs ? "Parcialmente Nublado" : "Partly Cloudy",
        emoji: isDay ? "⛅" : "☁️",
        iconType: "CloudSun",
      }
    case 3:
      return {
        label: isPt ? "Nublado" : isEs ? "Nublado" : "Overcast",
        emoji: "☁️",
        iconType: "Cloud",
      }
    case 45:
    case 48:
      return {
        label: isPt ? "Nevoeiro" : isEs ? "Niebla" : "Foggy",
        emoji: "🌫️",
        iconType: "CloudFog",
      }
    case 51:
    case 53:
    case 55:
      return {
        label: isPt ? "Chuvisco" : isEs ? "Llovizna" : "Drizzle",
        emoji: "🌦️",
        iconType: "CloudDrizzle",
      }
    case 56:
    case 57:
      return {
        label: isPt ? "Chuvisco Congelante" : isEs ? "Llovizna Helada" : "Freezing Drizzle",
        emoji: "🌨️",
        iconType: "CloudSnow",
      }
    case 61:
      return {
        label: isPt ? "Chuva Fraca" : isEs ? "Lluvia Ligera" : "Light Rain",
        emoji: "🌦️",
        iconType: "CloudRain",
      }
    case 63:
      return {
        label: isPt ? "Chuva Moderada" : isEs ? "Lluvia Moderada" : "Moderate Rain",
        emoji: "🌧️",
        iconType: "CloudRain",
      }
    case 65:
      return {
        label: isPt ? "Chuva Forte" : isEs ? "Lluvia Fuerte" : "Heavy Rain",
        emoji: "🌧️",
        iconType: "CloudRain",
      }
    case 71:
    case 73:
    case 75:
      return {
        label: isPt ? "Neve" : isEs ? "Nieve" : "Snow",
        emoji: "❄️",
        iconType: "Snowflake",
      }
    case 80:
    case 81:
    case 82:
      return {
        label: isPt ? "Pancadas de Chuva" : isEs ? "Chubascos" : "Rain Showers",
        emoji: "🌧️",
        iconType: "CloudRain",
      }
    case 95:
    case 96:
    case 99:
      return {
        label: isPt ? "Tempestade" : isEs ? "Tormenta" : "Thunderstorm",
        emoji: "⛈️",
        iconType: "CloudLightning",
      }
    default:
      return {
        label: isPt ? "Parcialmente Nublado" : isEs ? "Parcialmente Nublado" : "Partly Cloudy",
        emoji: "⛅",
        iconType: "CloudSun",
      }
  }
}

export async function fetchCityName(lat: number, lon: number): Promise<string> {
  try {
    const res = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=pt`,
      { cache: "no-store" }
    )
    if (res.ok) {
      const data = await res.json()
      const city = data.city || data.locality || data.principalSubdivision
      if (city) return city
    }
  } catch {}

  try {
    const res2 = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10`,
      { headers: { "Accept-Language": "pt-BR,pt;q=0.9" } }
    )
    if (res2.ok) {
      const data2 = await res2.json()
      const city2 = data2.address?.city || data2.address?.town || data2.address?.municipality || data2.address?.state
      if (city2) return city2
    }
  } catch {}

  return "Sua Localização"
}

export async function fetchWeatherData(lat: number, lon: number, cityName?: string, lang: string = "pt"): Promise<WeatherData> {
  const resolvedCityName = cityName || (await fetchCityName(lat, lon))

  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,is_day,precipitation,weather_code,wind_speed_10m&hourly=temperature_2m,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min,uv_index_max&timezone=auto&forecast_days=7`

  const response = await fetch(url, { cache: "no-store" })
  if (!response.ok) {
    throw new Error(`Open-Meteo API error: ${response.statusText}`)
  }

  const data = await response.json()
  const current = data.current
  const daily = data.daily
  const hourly = data.hourly

  const weatherCode = current.weather_code ?? 0
  const isDay = current.is_day === 1
  const weatherDetails = getWeatherDetails(weatherCode, current.is_day, lang)

  // Parse hourly (próximas 6 horas)
  const hourlyItems: HourlyForecastItem[] = []
  if (hourly && hourly.time && Array.isArray(hourly.time)) {
    const now = new Date()
    const currentHourIndex = hourly.time.findIndex((t: string) => {
      const d = new Date(t)
      return d.getTime() >= now.getTime() - 30 * 60 * 1000
    })

    const startIndex = currentHourIndex >= 0 ? currentHourIndex : 0
    for (let i = 0; i < 6; i++) {
      const idx = startIndex + i
      if (idx < hourly.time.length) {
        const timeStr = hourly.time[idx]
        const d = new Date(timeStr)
        const hourLabel = i === 0 ? "Agora" : `${d.getHours()}h`
        const tempVal = Math.round(hourly.temperature_2m[idx])
        const codeVal = hourly.weather_code[idx]
        const details = getWeatherDetails(codeVal, d.getHours() >= 6 && d.getHours() <= 18 ? 1 : 0, lang)

        hourlyItems.push({
          time: hourLabel,
          temp: tempVal,
          code: codeVal,
          emoji: details.emoji,
          label: details.label,
        })
      }
    }
  }

  // Parse daily (próximos 5 dias)
  const dailyItems: DailyForecastItem[] = []
  const weekDaysPt = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"]
  const weekDaysEs = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"]
  const weekDaysEn = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
  const weekDays = lang === "pt" ? weekDaysPt : lang === "es" ? weekDaysEs : weekDaysEn

  if (daily && daily.time && Array.isArray(daily.time)) {
    for (let i = 0; i < Math.min(5, daily.time.length); i++) {
      const dateStr = daily.time[i]
      const d = new Date(`${dateStr}T12:00:00`)
      const dayName = i === 0 ? (lang === "pt" ? "Hoje" : lang === "es" ? "Hoy" : "Today") : weekDays[d.getDay()]
      const minTemp = Math.round(daily.temperature_2m_min[i])
      const maxTemp = Math.round(daily.temperature_2m_max[i])
      const code = daily.weather_code[i]
      const details = getWeatherDetails(code, 1, lang)

      dailyItems.push({
        date: dateStr,
        dayName,
        minTemp,
        maxTemp,
        code,
        condition: details.label,
        emoji: details.emoji,
      })
    }
  }

  const result: WeatherData = {
    cityName: resolvedCityName,
    currentTemp: Math.round(current.temperature_2m),
    apparentTemp: Math.round(current.apparent_temperature ?? current.temperature_2m),
    conditionLabel: weatherDetails.label,
    conditionEmoji: weatherDetails.emoji,
    weatherCode,
    isDay,
    humidity: Math.round(current.relative_humidity_2m ?? 0),
    windSpeed: Math.round(current.wind_speed_10m ?? 0),
    uvIndex: Math.round(daily?.uv_index_max?.[0] ?? 2),
    minTempToday: Math.round(daily?.temperature_2m_min?.[0] ?? current.temperature_2m - 4),
    maxTempToday: Math.round(daily?.temperature_2m_max?.[0] ?? current.temperature_2m + 4),
    hourly: hourlyItems,
    daily: dailyItems,
    lastUpdated: Date.now(),
  }

  return result
}
